import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { themeToCssVars, type Theme } from '@ce/theme';

const ThemeContext = createContext<Theme | null>(null);

export function useTheme(): Theme {
  const theme = useContext(ThemeContext);
  if (!theme) throw new Error('useTheme called outside ThemeProvider');
  return theme;
}

const STYLE_ID = 'ce-theme-vars';
const FONT_ID = 'ce-theme-font';

/**
 * Writes the theme into a single style tag rather than into React state on
 * every node. A CMS theme push then repaints the whole app without unmounting
 * anything — which matters mid-convention, when an attendee may be halfway
 * through a booking when the client changes their accent colour.
 */
export function ThemeProvider({ theme, children }: { theme: Theme; children: ReactNode }) {
  const css = useMemo(() => themeToCssVars(theme), [theme]);

  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      document.head.appendChild(tag);
    }
    tag.textContent = css;
  }, [css]);

  useEffect(() => {
    const url = theme.type.webFontUrl;
    if (!url) return;
    let link = document.getElementById(FONT_ID) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = FONT_ID;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== url) link.href = url;
  }, [theme.type.webFontUrl]);

  useEffect(() => {
    document.documentElement.style.background = theme.colors.bg;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme.colors.bg);
    document.title = `${theme.brand.name} — ${theme.brand.tagline}`;
  }, [theme.colors.bg, theme.brand.name, theme.brand.tagline]);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}
